import PropTypes from "prop-types";

/**
 * RealisticMSB – High-fidelity 3D Main Switch Board (MSB) component.
 * Features:
 * - Dynamic Phase Lights (R-Y-B) that scale with live power/voltage value (all 3 ON at max, turning off sequentially as power drops)
 * - 3 Analog voltmeters with needles centered inside each dial face (pivoted at dial base y=145, length 15px)
 * - Cleaned static dial faces: zero leftover needle or screw marks
 * - Enlarged, highly visible pilot lamps with LED specular halo and glow
 */
const RealisticMSB = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor = "grey",
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const isEnergized = isRunning && !isOffline;
  const isTrip = statusColor === "red" && !isOffline;
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  // Power level ratio (0.0 to 1.0) strictly based on percentage ratio
  const powerRatio = isEnergized ? (typeof ratio === "number" ? ratio : 1) : 0;

  // Number of active phases (0 to 3) based on power:
  // > 66% -> 3 phases ON (R, Y, B)
  // 33% - 66% -> 2 phases ON (R, Y)
  // > 0% - 33% -> 1 phase ON (R)
  // 0% or offline -> 0 phases ON
  const activePhases = powerRatio >= 0.66 ? 3 : powerRatio >= 0.33 ? 2 : powerRatio > 0 ? 1 : 0;

  // Deflection angles for the 3 analog meter needles:
  // At 0 power: -34deg (resting on left 0 mark)
  // At max power: +34deg (full deflection to right)
  const angle1 = activePhases >= 1 ? -34 + Math.min(1, powerRatio / 0.33) * 68 : -34;
  const angle2 = activePhases >= 2 ? -34 + Math.min(1, (powerRatio - 0.33) / 0.33) * 68 : -34;
  const angle3 = activePhases >= 3 ? -34 + Math.min(1, (powerRatio - 0.66) / 0.34) * 68 : -34;

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/MSB.png"
        alt="SCADA Realistic MSB"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 355x491 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 355 491"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="msbLedGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── 3 Top Phase Indicator Pilot Lamps (Enlarged and Vibrant) ── */}
        {/* Lamp 1 (Red / Phase R / Trip Indicator: cx: 103, cy: 79) */}
        <g>
          {(activePhases >= 1 || isTrip) && (
            <circle
              cx="103"
              cy="79"
              r="15"
              fill="#ef4444"
              opacity="0.45"
              filter="url(#msbLedGlow)"
            />
          )}
          <circle
            cx="103"
            cy="79"
            r="11"
            fill={activePhases >= 1 || isTrip ? "#ef4444" : "#450a0a"}
            stroke={activePhases >= 1 || isTrip ? "#fecaca" : "#292524"}
            strokeWidth="1.8"
            style={{
              animation: activePhases >= 1 ? "scada-led-blink 2.2s infinite ease-in-out" : "none",
            }}
          />
          {(activePhases >= 1 || isTrip) && <circle cx="100.5" cy="76.5" r="3.2" fill="#ffffff" opacity="0.85" />}
        </g>

        {/* Lamp 2 (Yellow / Phase Y: cx: 177, cy: 79) */}
        <g>
          {activePhases >= 2 && (
            <circle
              cx="177"
              cy="79"
              r="15"
              fill="#eab308"
              opacity="0.45"
              filter="url(#msbLedGlow)"
            />
          )}
          <circle
            cx="177"
            cy="79"
            r="11"
            fill={activePhases >= 2 ? "#facc15" : "#422006"}
            stroke={activePhases >= 2 ? "#fef08a" : "#292524"}
            strokeWidth="1.8"
            style={{
              animation: activePhases >= 2 ? "scada-led-blink 1.8s infinite 0.3s ease-in-out" : "none",
            }}
          />
          {activePhases >= 2 && <circle cx="174.5" cy="76.5" r="3.2" fill="#ffffff" opacity="0.85" />}
        </g>

        {/* Lamp 3 (Blue / Phase B: cx: 252, cy: 79) */}
        <g>
          {activePhases >= 3 && (
            <circle
              cx="252"
              cy="79"
              r="15"
              fill="#3b82f6"
              opacity="0.45"
              filter="url(#msbLedGlow)"
            />
          )}
          <circle
            cx="252"
            cy="79"
            r="11"
            fill={activePhases >= 3 ? "#3b82f6" : "#0f172a"}
            stroke={activePhases >= 3 ? "#bfdbfe" : "#292524"}
            strokeWidth="1.8"
            style={{
              animation: activePhases >= 3 ? "scada-led-blink 2s infinite 0.6s ease-in-out" : "none",
            }}
          />
          {activePhases >= 3 && <circle cx="249.5" cy="76.5" r="3.2" fill="#ffffff" opacity="0.85" />}
        </g>

        {/* ── 3 Analog Meter Moving Needles (Sitting neatly inside dial window) ── */}
        {/* Meter 1 (cx: 100, cy: 145) */}
        <g style={{ transformOrigin: "100px 145px", transform: `rotate(${angle1}deg)` }}>
          <line
            x1="100"
            y1="145"
            x2="100"
            y2="130"
            stroke="#dc2626"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <circle cx="100" cy="145" r="2.8" fill="#1e293b" stroke="#dc2626" strokeWidth="0.8" />
        </g>

        {/* Meter 2 (cx: 179, cy: 145) */}
        <g style={{ transformOrigin: "179px 145px", transform: `rotate(${angle2}deg)` }}>
          <line
            x1="179"
            y1="145"
            x2="179"
            y2="130"
            stroke="#dc2626"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <circle cx="179" cy="145" r="2.8" fill="#1e293b" stroke="#dc2626" strokeWidth="0.8" />
        </g>

        {/* Meter 3 (cx: 259, cy: 145) */}
        <g style={{ transformOrigin: "259px 145px", transform: `rotate(${angle3}deg)` }}>
          <line
            x1="259"
            y1="145"
            x2="259"
            y2="130"
            stroke="#dc2626"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <circle cx="259" cy="145" r="2.8" fill="#1e293b" stroke="#dc2626" strokeWidth="0.8" />
        </g>
      </svg>
    </div>
  );
};

RealisticMSB.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticMSB;
